import { Formik, Form } from "formik";
import React, { useEffect, useState } from "react";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import {
  ICFormikDropDown,
  ICFormikTextInput,
} from "../../core-component/formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  handleError,
  hasAnyModification,
  sweetAlertError,
} from "../../utils/AppFunctions";
import Loader from "../../core-component/ui/Loader";

import { IOption } from "../../data/AppInterface";
import { IMAGE_TYPE } from "../../utils/AppEnumerations";
import { ICFileUpload } from "../../core-component/form";
import { IGalleryDirectory } from "./GalleryDirectoryListController";
import {
  fetchGalleryDirectoryById,
  GALLERY_DIRECTORY_INITIAL_VALUES,
  galleryDirectoryValidationSchema,
  handleSubmitGalleryDirectory,
  IGalleryDirectoryData,
} from "./GalleryDirectoryFormController";
import { getGenderDdl } from "../measurement-type/MeasurementTypeListController";
import { fetchUploadImageService } from "../global/image-service/ImageServices";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";

interface IGalleryDirectoryFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  galleryDirectoryToEdit?: IGalleryDirectory;
}

const GalleryDirectoryForm: React.FC<IGalleryDirectoryFormProps> = ({
  handleClose,
  galleryDirectoryToEdit,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [gender, setGender] = useState<IOption<string>[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folderImage, setFolderImage] = useState<string[]>([]);
  const [
    galleryDirectoryFormInitialValues,
    setGalleryDirectoryFormInitialValues,
  ] = useState<IGalleryDirectoryData>();

  useEffect(() => {
    setFolderImage(
      galleryDirectoryFormInitialValues?.folder_image
        ? [galleryDirectoryFormInitialValues.folder_image]
        : []
    );
  }, [galleryDirectoryFormInitialValues]);

  useEffect(() => {
    if (galleryDirectoryToEdit) {
      getGalleryDirectoryById(galleryDirectoryToEdit.id);
    } else {
      setGalleryDirectoryFormInitialValues(GALLERY_DIRECTORY_INITIAL_VALUES);
      fetchGender();
    }
  }, []);

  const getGalleryDirectoryById = async (id: string) => {
    setLoading(true);
    const galleryDirectory = await fetchGalleryDirectoryById(id, handleClose);
    setGalleryDirectoryFormInitialValues(galleryDirectory);
    fetchGender(galleryDirectory.gender);
    setLoading(false);
  };

  const fetchGender = async (genderValue?: string) => {
    await getGenderDdl(setGender, genderValue);
  };

  const checkFormModification = (values: IGalleryDirectoryData) => {
    if (
      galleryDirectoryFormInitialValues &&
      hasAnyModification(values, galleryDirectoryFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const handleSubmitDirectory = async (values: IGalleryDirectoryData) => {
    try {
      setSubmitLoading(true);
      const updatedTypeMedia = folderImage
        ? folderImage.map((url) => ({
            media_url: url,
          }))
        : [];

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append(`ImageFile`, file);
        formData.append(`type`, IMAGE_TYPE.GalleryFolder);
        values.id && formData.append(`typeId`, values.id.toString());

        const mediaUrl = await fetchUploadImageService(formData);
        if (!mediaUrl) {
          return null;
        }

        const newMediaItem = {
          media_url: mediaUrl,
        };
        updatedTypeMedia.push(newMediaItem);
      }

      await handleSubmitGalleryDirectory(
        {
          ...values,
          folder_image: updatedTypeMedia[0]?.media_url || "",
        },
        setSubmitLoading,
        galleryDirectoryToEdit?.id,
        handleClose
      );
    } catch (e: any) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
      handleError(e, "galleryDirectorySubmit");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {galleryDirectoryFormInitialValues && (
            <Formik
              initialValues={galleryDirectoryFormInitialValues}
              validationSchema={galleryDirectoryValidationSchema(t)}
              onSubmit={(values) => {
                handleSubmitDirectory(values);
              }}
            >
              {(formikPorps) => {
                const { handleSubmit, values } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/4"
                    onClose={() => checkFormModification(values)}
                    title={t("gallery_directory")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full "
                    >
                      <div className="flex-1 overflow-y-scroll pt-2.5 mb-4 space-y-4">
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"folder_name"}
                          placeholder={t("folder_name")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />

                        <ICFormikDropDown
                          {...formikPorps}
                          containerFormikClassName="mt-4"
                          name={"gender"}
                          placeholder={t("gender")}
                          options={gender}
                          required
                          disabled={readOnly}
                        />

                        <div className="mt-4">
                          <ICFileUpload
                            title={t("upload_image")}
                            maxFiles={1}
                            maxFileSizeMB={10}
                            acceptedFileTypes={["jpg", "png", "jpeg"]}
                            onFilesSelected={setSelectedFiles}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            data={folderImage}
                            setData={setFolderImage}
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className={"w-full"}
                          children={t("submit")}
                        />
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default GalleryDirectoryForm;
